import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { makeIdeaBulb } from "../../../../../components/LandingPage/v2/particleShapes";

function hexToRgb(hex) {
	const n = parseInt(hex.slice(1), 16);
	return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const VERTEX_SHADER = `
attribute vec3 aPos;
attribute vec4 aSeed; // x: size mult, y: twinkle phase, z: twinkle speed, w: white mix
attribute vec3 aTint; // per-particle brand color (used by the logo shape)
uniform vec2 uResolution;
uniform vec2 uCenter;
uniform float uRadius;
uniform float uYaw;
uniform float uTilt;
uniform float uFocal;
uniform vec2 uCam;
uniform float uTime;
uniform float uDpr;
uniform float uFade;
uniform float uBoost;
varying float vAlpha;
varying float vShade;
varying vec3 vTint;

void main() {
	vTint = aTint;
	float cy = cos(uYaw);
	float sy = sin(uYaw);
	float ct = cos(uTilt);
	float st = sin(uTilt);
	vec3 p = aPos;
	vec3 r1 = vec3(p.x * cy + p.z * sy, p.y, -p.x * sy + p.z * cy);
	vec3 r2 = vec3(r1.x, r1.y * ct - r1.z * st, r1.y * st + r1.z * ct);
	float scale = uFocal / (uFocal + r2.z);
	vec2 px = uCenter + r2.xy * uRadius * scale + uCam * 20.0 * scale;

	vec2 ndc = (px / uResolution) * 2.0 - 1.0;
	ndc.y = -ndc.y;
	gl_Position = vec4(ndc, 0.0, 1.0);

	float twinkle = 0.72 + 0.28 * sin(uTime * aSeed.z + aSeed.y);
	vAlpha = max(0.0, (0.85 - r2.z * 0.55)) * twinkle * uFade * (1.0 + uBoost * 0.8);
	vShade = aSeed.w + uBoost * 0.35;
	gl_PointSize = max(1.0, (2.3 * scale - r2.z * 0.5) * aSeed.x * uDpr);
}
`;

const FRAGMENT_SHADER = `
precision mediump float;
uniform vec3 uColor;
uniform float uTintBlend;
varying float vAlpha;
varying float vShade;
varying vec3 vTint;

void main() {
	vec2 uv = gl_PointCoord * 2.0 - 1.0;
	float d = dot(uv, uv);
	if (d > 1.0) discard;
	float glow = exp(-d * 3.2);
	vec3 base = mix(uColor, vTint, uTintBlend);
	vec3 col = mix(base, vec3(1.0), clamp(vShade, 0.0, 1.0) * 0.7 * (1.0 - uTintBlend * 0.55));
	gl_FragColor = vec4(col * glow * vAlpha, glow * vAlpha);
}
`;

// ---- shape generators (unit-ish scale, centered at origin) ---------------

// scene space is y-down (matches the screen-pixel space the shader projects
// into), so "up" is negative y in every generator below

// makeIdeaBulb lives in v2/particleShapes.js, shared with the
// business-division stage sculpture

/** four bust silhouettes joined by a ring — "nothing is built alone" */
function makePeopleRing(count) {
	const pts = new Float32Array(count * 3);
	const PEOPLE = 4;
	const R = 0.62;
	const nRing = Math.floor(count * 0.1);
	const perPerson = Math.floor((count - nRing) / PEOPLE);
	let i = 0;
	for (let p = 0; p < PEOPLE; p++) {
		const a = (p / PEOPLE) * Math.PI * 2;
		const cx = Math.cos(a) * R;
		const cz = Math.sin(a) * R;
		const nHead = Math.floor(perPerson * 0.38);
		for (let k = 0; k < perPerson; k++, i++) {
			const phi = Math.random() * Math.PI * 2;
			const ct = Math.random() * 2 - 1;
			const st = Math.sqrt(1 - ct * ct);
			const j = 0.94 + Math.random() * 0.1;
			if (k < nHead) {
				// head
				const r = 0.15 * j;
				pts[i * 3] = cx + Math.cos(phi) * st * r;
				pts[i * 3 + 1] = -0.38 + ct * r;
				pts[i * 3 + 2] = cz + Math.sin(phi) * st * r;
			} else {
				// torso: egg-shaped shell under the head
				pts[i * 3] = cx + Math.cos(phi) * st * 0.3 * j;
				pts[i * 3 + 1] = 0.18 + ct * 0.45 * j;
				pts[i * 3 + 2] = cz + Math.sin(phi) * st * 0.22 * j;
			}
		}
	}
	// the ring that binds everyone together, at shoulder height
	for (; i < count; i++) {
		const a = Math.random() * Math.PI * 2;
		const r = R * (1 + (Math.random() - 0.5) * 0.05);
		pts[i * 3] = Math.cos(a) * r;
		pts[i * 3 + 1] = -0.05 + (Math.random() - 0.5) * 0.04;
		pts[i * 3 + 2] = Math.sin(a) * r;
	}
	return pts;
}

// the six triangles of the Verzel symbol (viewBox 0 0 140 102.666 of
// logo-verzel-symbol-white.svg) with each face's brand color; the dark
// faces use a dim slate so the 3D shading still reads on a black canvas
const LOGO_TRIS = [
	{ p: [[52.85, 64.49], [0, 95.15], [52.85, 0.78]], c: [0.49, 0.447, 1.0] },
	{ p: [[52.85, 64.49], [105.7, 95.15], [52.85, 0.78]], c: [0.039, 0.882, 1.0] },
	{ p: [[105.7, 95.15], [52.85, 64.49], [0, 95.15]], c: [0.34, 0.4, 0.54] },
	{ p: [[113.41, 58.41], [62.81, 58.29], [140, 12.84]], c: [0.808, 1.0, 0.0] },
	{ p: [[113.41, 58.41], [138.42, 102.4], [140, 12.84]], c: [0.416, 0.69, 0.149] },
	{ p: [[138.42, 102.4], [113.41, 58.41], [62.81, 58.29]], c: [0.34, 0.4, 0.54] },
];

/** the Verzel symbol as a thin particle slab, with per-face brand tints */
function makeVerzelLogo(count) {
	const pts = new Float32Array(count * 3);
	const tints = new Float32Array(count * 3);
	const areas = LOGO_TRIS.map(({ p }) => {
		const [a, b, c] = p;
		return Math.abs(
			(b[0] - a[0]) * (c[1] - a[1]) - (c[0] - a[0]) * (b[1] - a[1]),
		);
	});
	const totalArea = areas.reduce((s, a) => s + a, 0);
	// SVG y grows downward and so does the screen-pixel space the vertex
	// shader projects into, so y passes through unflipped
	const toScene = (x, y) => [(x - 70) / 78, (y - 51.33) / 78];

	let i = 0;
	for (let t = 0; t < LOGO_TRIS.length; t++) {
		const { p, c } = LOGO_TRIS[t];
		const [a, b, cc] = p;
		const n =
			t === LOGO_TRIS.length - 1
				? count - i
				: Math.round((areas[t] / totalArea) * count);
		for (let k = 0; k < n && i < count; k++, i++) {
			let x;
			let y;
			if (k % 5 === 0) {
				// edge highlight: sample the triangle outline for crisp silhouettes
				const edge = [[a, b], [b, cc], [cc, a]][(Math.random() * 3) | 0];
				const s = Math.random();
				x = edge[0][0] + (edge[1][0] - edge[0][0]) * s;
				y = edge[0][1] + (edge[1][1] - edge[0][1]) * s;
			} else {
				let u = Math.random();
				let v = Math.random();
				if (u + v > 1) {
					u = 1 - u;
					v = 1 - v;
				}
				x = a[0] + (b[0] - a[0]) * u + (cc[0] - a[0]) * v;
				y = a[1] + (b[1] - a[1]) * u + (cc[1] - a[1]) * v;
			}
			const [sx, sy] = toScene(x, y);
			pts[i * 3] = sx + (Math.random() - 0.5) * 0.012;
			pts[i * 3 + 1] = sy + (Math.random() - 0.5) * 0.012;
			pts[i * 3 + 2] = (Math.random() - 0.5) * 0.11;
			const lift = k % 5 === 0 ? 0.3 : 0;
			const shade = 0.88 + Math.random() * 0.24;
			tints[i * 3] = Math.min(1, c[0] * shade + lift);
			tints[i * 3 + 1] = Math.min(1, c[1] * shade + lift);
			tints[i * 3 + 2] = Math.min(1, c[2] * shade + lift);
		}
	}
	return { pts, tints };
}

/** far-field ambient stars, spread across the whole hero */
function makeStars(count) {
	const pts = new Float32Array(count * 3);
	for (let i = 0; i < count; i++) {
		pts[i * 3] = (Math.random() * 2 - 1) * 1.6;
		pts[i * 3 + 1] = (Math.random() * 2 - 1) * 1.1;
		pts[i * 3 + 2] = Math.random() * 2 - 1;
	}
	return pts;
}

function makeSeeds(count, { sizeMin, sizeMax, whiteMix }) {
	const seeds = new Float32Array(count * 4);
	for (let i = 0; i < count; i++) {
		seeds[i * 4] = sizeMin + Math.random() * (sizeMax - sizeMin);
		seeds[i * 4 + 1] = Math.random() * Math.PI * 2;
		seeds[i * 4 + 2] = 0.6 + Math.random() * 2.2;
		seeds[i * 4 + 3] = Math.random() < 0.16 ? whiteMix : Math.random() * 0.18;
	}
	return seeds;
}

function compileProgram(gl) {
	const make = (type, src) => {
		const s = gl.createShader(type);
		gl.shaderSource(s, src);
		gl.compileShader(s);
		if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
			throw new Error(gl.getShaderInfoLog(s) || "shader compile failed");
		}
		return s;
	};
	const program = gl.createProgram();
	gl.attachShader(program, make(gl.VERTEX_SHADER, VERTEX_SHADER));
	gl.attachShader(program, make(gl.FRAGMENT_SHADER, FRAGMENT_SHADER));
	gl.linkProgram(program);
	if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
		throw new Error(gl.getProgramInfoLog(program) || "program link failed");
	}
	return program;
}

/**
 * Real-time WebGL hero: a cloud of additive-blended particles that morphs
 * between three 3D sculptures matching each slide's message — an idea
 * lightbulb ("transforme suas ideias"), four busts joined by a ring ("nosso
 * potencial está nas pessoas") and the Verzel symbol in its brand colors —
 * over a slow far-field starscape.
 * Reacts to the cursor (camera parallax) and lerps
 * its color toward the active slide accent. Falls back to a static frame for
 * prefers-reduced-motion, and to nothing when WebGL is unavailable.
 */
export default function HeroScene({ accent, shape = 0 }) {
	const canvasRef = useRef(null);
	const accentRef = useRef(hexToRgb(accent));
	const shapeRef = useRef(shape);

	useEffect(() => {
		accentRef.current = hexToRgb(accent);
	}, [accent]);

	useEffect(() => {
		shapeRef.current = shape;
	}, [shape]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return undefined;

		let gl;
		try {
			gl = canvas.getContext("webgl", {
				alpha: true,
				antialias: false,
				depth: false,
				stencil: false,
				powerPreference: "high-performance",
			});
		} catch (e) {
			gl = null;
		}
		if (!gl) return undefined;

		const reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const isMobile = window.innerWidth < 992;
		const dpr = Math.min(window.devicePixelRatio || 1, 2);

		// mobile density doubled for a richer, less sparse sculpture; still well
		// under the desktop count so the additive glows don't saturate to white
		const COUNT = isMobile ? 4200 : 9500;
		const STAR_COUNT = isMobile ? 320 : 420;

		let program;
		try {
			program = compileProgram(gl);
		} catch (e) {
			return undefined;
		}
		gl.useProgram(program);
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.ONE, gl.ONE);

		const loc = {
			aPos: gl.getAttribLocation(program, "aPos"),
			aSeed: gl.getAttribLocation(program, "aSeed"),
			aTint: gl.getAttribLocation(program, "aTint"),
		};
		const uniforms = {};
		for (const name of [
			"uResolution",
			"uCenter",
			"uRadius",
			"uYaw",
			"uTilt",
			"uFocal",
			"uCam",
			"uTime",
			"uDpr",
			"uFade",
			"uBoost",
			"uColor",
			"uTintBlend",
		]) {
			uniforms[name] = gl.getUniformLocation(program, name);
		}

		// ---- geometry -----------------------------------------------------
		const logo = makeVerzelLogo(COUNT);
		const bulb = makeIdeaBulb(COUNT);
		// hero-only: sink the bulb a bit further so its visual mass lines up
		// with the copy block on the left (the shared shape stays centered
		// for the business-division stage)
		for (let i = 1; i < bulb.length; i += 3) bulb[i] += 0.12;
		const shapes = [bulb, makePeopleRing(COUNT), logo.pts];
		const LOGO_SHAPE = 2;
		const current = new Float32Array(shapes[shapeRef.current % 3]);
		let target = shapes[shapeRef.current % 3];
		// per-particle morph speed → organic, trailing transitions
		const morphSpeed = new Float32Array(COUNT);
		for (let i = 0; i < COUNT; i++) morphSpeed[i] = 0.028 + Math.random() * 0.05;

		const seeds = makeSeeds(COUNT, { sizeMin: 0.8, sizeMax: 2.1, whiteMix: 0.9 });
		const starPos = makeStars(STAR_COUNT);
		const starSeeds = makeSeeds(STAR_COUNT, {
			sizeMin: 0.5,
			sizeMax: 1.15,
			whiteMix: 0.5,
		});

		const posBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, current, gl.DYNAMIC_DRAW);

		const seedBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, seedBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, seeds, gl.STATIC_DRAW);

		const starPosBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, starPosBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, starPos, gl.STATIC_DRAW);

		const starSeedBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, starSeedBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, starSeeds, gl.STATIC_DRAW);

		const tintBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, tintBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, logo.tints, gl.STATIC_DRAW);

		gl.enableVertexAttribArray(loc.aPos);
		gl.enableVertexAttribArray(loc.aSeed);
		gl.enableVertexAttribArray(loc.aTint);

		// ---- state ----------------------------------------------------------
		let width = 0;
		let height = 0;
		let rafId = null;
		let running = false;
		let contextLost = false;
		let time = 0;
		let yaw = 0;
		let starYaw = 0;
		let boost = 0;
		let activeShape = shapeRef.current % 3;
		// 0 → free-spinning abstract sculpture, 1 → logo settled facing front
		let logoBlend = activeShape === LOGO_SHAPE ? 1 : 0;

		const mouse = { x: 0, y: 0 };
		const cam = { x: 0, y: 0 };
		const col = [...accentRef.current];

		// live top edge of the bottom-anchored hero copy (relative to the hero),
		// so the mobile sculpture can sit centered in the usable band above it
		let contentTop = 0;
		function measureContent() {
			const parent = canvas.parentElement;
			const content = parent && parent.querySelector(".content");
			if (parent && content) {
				const pr = parent.getBoundingClientRect();
				contentTop = content.getBoundingClientRect().top - pr.top;
			} else {
				contentTop = height * 0.55;
			}
		}

		function resize() {
			const rect = canvas.parentElement.getBoundingClientRect();
			width = rect.width;
			height = rect.height;
			canvas.width = Math.round(width * dpr);
			canvas.height = Math.round(height * dpr);
			gl.viewport(0, 0, canvas.width, canvas.height);
			measureContent();
		}
		resize();
		// the first measure can catch the fallback font's line height; re-measure
		// once the real font settles so the band is right from the first paint
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(measureContent).catch(() => {});
		}

		function bindDraw(posBuf, seedBuf, count) {
			gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
			gl.vertexAttribPointer(loc.aPos, 3, gl.FLOAT, false, 0, 0);
			gl.bindBuffer(gl.ARRAY_BUFFER, seedBuf);
			gl.vertexAttribPointer(loc.aSeed, 4, gl.FLOAT, false, 0, 0);
			gl.bindBuffer(gl.ARRAY_BUFFER, tintBuffer);
			gl.vertexAttribPointer(loc.aTint, 3, gl.FLOAT, false, 0, 0);
			gl.drawArrays(gl.POINTS, 0, count);
		}

		function draw(dt) {
			time += dt;
			cam.x += (mouse.x - cam.x) * 0.04;
			cam.y += (mouse.y - cam.y) * 0.04;
			const targetCol = accentRef.current;
			for (let c = 0; c < 3; c++) col[c] += (targetCol[c] - col[c]) * 0.035;

			// shape switch → retarget and flash a brightness boost
			const wanted = shapeRef.current % 3;
			if (wanted !== activeShape) {
				activeShape = wanted;
				target = shapes[wanted];
				boost = 1;
				// slide copy differs in length → the usable band shifts
				measureContent();
			}
			boost *= 0.985;

			// CPU morph with per-particle speed (organic trailing edge)
			for (let i = 0; i < COUNT; i++) {
				const s = morphSpeed[i];
				const i3 = i * 3;
				current[i3] += (target[i3] - current[i3]) * s;
				current[i3 + 1] += (target[i3 + 1] - current[i3 + 1]) * s;
				current[i3 + 2] += (target[i3 + 2] - current[i3 + 2]) * s;
			}
			gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
			gl.bufferSubData(gl.ARRAY_BUFFER, 0, current);

			logoBlend +=
				((activeShape === LOGO_SHAPE ? 1 : 0) - logoBlend) * 0.035 * dt * 60;

			// abstract shapes spin freely; the logo decelerates into a gentle
			// front-facing sway so the mark stays readable
			yaw += (0.0022 + cam.x * 0.0012) * dt * 60 * (1 - logoBlend);
			if (logoBlend > 0.01) {
				const twoPi = Math.PI * 2;
				const settled =
					Math.round(yaw / twoPi) * twoPi + Math.sin(time * 0.55) * 0.18;
				yaw += (settled - yaw) * 0.05 * logoBlend * dt * 60;
			}
			starYaw += 0.00035 * dt * 60;

			const parallaxY = window.scrollY * -0.07;
			const cx = isMobile ? width * 0.5 : width * 0.72;
			const radius = isMobile
				? Math.min(width, height) * 0.37
				: Math.min(width * 0.21, height * 0.33);
			// mobile: center the sculpture in the usable band between the navbar
			// and the bottom-anchored copy, measured live so it tracks the real
			// content/viewport size — a fixed fraction only lined up when the band
			// happened to be large. Clamped so a tall slide can't clip it off the
			// top nor a short one drop it too low.
			const TOP_INSET = 56; // clears the fixed navbar
			const cy = isMobile
				? Math.min(
						height * 0.4,
						Math.max(radius * 0.62, (TOP_INSET + contentTop) / 2),
					) + parallaxY
				: height * 0.46 + parallaxY;

			gl.clearColor(0, 0, 0, 0);
			gl.clear(gl.COLOR_BUFFER_BIT);

			gl.uniform2f(uniforms.uResolution, width, height);
			gl.uniform1f(uniforms.uTime, time);
			gl.uniform1f(uniforms.uDpr, dpr);
			gl.uniform2f(uniforms.uCam, cam.x, cam.y);
			gl.uniform3f(uniforms.uColor, col[0], col[1], col[2]);

			// far-field stars: full-hero spread
			gl.uniform2f(uniforms.uCenter, width * 0.5, height * 0.5 + parallaxY * 0.4);
			gl.uniform1f(uniforms.uRadius, Math.max(width, height) * 0.62);
			gl.uniform1f(uniforms.uYaw, starYaw);
			gl.uniform1f(uniforms.uTilt, 0.2);
			gl.uniform1f(uniforms.uFocal, 6.0);
			gl.uniform1f(uniforms.uFade, isMobile ? 0.3 : 0.55);
			gl.uniform1f(uniforms.uBoost, 0);
			gl.uniform1f(uniforms.uTintBlend, 0);
			bindDraw(starPosBuffer, starSeedBuffer, STAR_COUNT);

			// morphing sculpture
			gl.uniform2f(uniforms.uCenter, cx, cy);
			gl.uniform1f(uniforms.uRadius, radius);
			gl.uniform1f(uniforms.uYaw, yaw);
			gl.uniform1f(
				uniforms.uTilt,
				0.42 * (1 - logoBlend) +
					0.04 * logoBlend +
					cam.y * 0.25 * (1 - logoBlend * 0.6),
			);
			gl.uniform1f(uniforms.uFocal, 4.2);
			gl.uniform1f(uniforms.uFade, isMobile ? 0.75 : 1.0);
			gl.uniform1f(uniforms.uBoost, boost);
			gl.uniform1f(uniforms.uTintBlend, logoBlend);
			bindDraw(posBuffer, seedBuffer, COUNT);
		}

		let last = 0;
		function loop(now) {
			rafId = requestAnimationFrame(loop);
			const dt = last ? Math.min((now - last) / 1000, 0.05) : 1 / 60;
			last = now;
			// skip work while the hero is scrolled out of view
			if (window.scrollY < height * 1.1) draw(dt);
		}

		function start() {
			if (running || reducedMotion || contextLost) return;
			running = true;
			last = 0;
			rafId = requestAnimationFrame(loop);
		}

		function stop() {
			running = false;
			if (rafId) cancelAnimationFrame(rafId);
			rafId = null;
		}

		function onPointerMove(e) {
			mouse.x = (e.clientX / width) * 2 - 1;
			mouse.y = (e.clientY / height) * 2 - 1;
		}

		function onVisibility() {
			if (document.hidden) stop();
			else start();
		}

		function onResize() {
			resize();
			if (reducedMotion) draw(1 / 60);
		}

		function onContextLost(e) {
			e.preventDefault();
			contextLost = true;
			stop();
		}

		window.addEventListener("pointermove", onPointerMove, { passive: true });
		window.addEventListener("resize", onResize);
		document.addEventListener("visibilitychange", onVisibility);
		canvas.addEventListener("webglcontextlost", onContextLost);

		if (reducedMotion) {
			draw(1 / 60); // single static frame
		} else {
			start();
		}

		return () => {
			stop();
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("resize", onResize);
			document.removeEventListener("visibilitychange", onVisibility);
			canvas.removeEventListener("webglcontextlost", onContextLost);
			gl.deleteBuffer(posBuffer);
			gl.deleteBuffer(seedBuffer);
			gl.deleteBuffer(starPosBuffer);
			gl.deleteBuffer(starSeedBuffer);
			gl.deleteBuffer(tintBuffer);
			gl.deleteProgram(program);
		};
	}, []);

	return <Canvas ref={canvasRef} aria-hidden="true" />;
}

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`;
